import { useState } from "preact/hooks";
import { FolderPlusIcon, UserPlusIcon } from "@heroicons/react/24/outline";
import { api, ApiError } from "../lib/api-client";
import { adminHref } from "../lib/platform-admin";
import { useAsyncData } from "../lib/use-async-data";
import { useConfirm } from "../lib/use-confirm";
import { useToast } from "../lib/use-toast";
import {
  asRateLimitPayload,
  inviteRateLimitMessage,
} from "../lib/rate-limit-message";
import { UserList, type UserListLayout } from "../components/user-list";
import type { UserRowDto } from "@shared/types";
import {
  Alert,
  Button,
  Card,
  CardBody,
  EmptyState,
  Field,
  Input,
  PageHeader,
  SectionHeading,
  SkeletonList,
} from "../components/ui";

export function AdminUsersPage() {
  const { data: items, error, reload, setError } = useAsyncData(
    () => api.listUsers().then((res) => res.users),
    [],
  );
  const [submitting, setSubmitting] = useState(false);

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [projectSlug, setProjectSlug] = useState("");
  const [projectName, setProjectName] = useState("");
  const { confirm, dialog: confirmDialog } = useConfirm();
  const toast = useToast();

  async function onInvite(e: Event) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const target = email;
      const slug = projectSlug;
      const res = await api.inviteUser({
        email,
        name: name || undefined,
        projectSlug,
        projectName,
      });
      setEmail("");
      setName("");
      setProjectSlug("");
      setProjectName("");
      reload();
      // Spell out whether an existing account was reused, so a typo that hits
      // someone else's address doesn't pass silently.
      const created = res.isNewUser
        ? `${target} のユーザーとプロジェクト /${slug} を作成しました`
        : `既存ユーザー ${target} を owner にしてプロジェクト /${slug} を作成しました`;
      if (!res.emailSent) {
        // Same distinction as the project invite screen: a quota exhaustion
        // needs waiting, not a manual resend that will also fail.
        toast.warning(
          res.emailErrorCode === "quota_exhausted"
            ? `${created}が、メールの送信枠を使い切っているためサインインリンクを送れませんでした。枠が回復してからサインインリンクを送り直してください。`
            : `${created}が、サインインリンクの送信に失敗しました。Better Auth の magic link を手動で送り直してください。`,
          { duration: 8000 },
        );
      } else {
        toast.success(`${created}。サインインリンクを送信しました。`);
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 429) {
        setError(inviteRateLimitMessage(asRateLimitPayload(e.payload)));
        return;
      }
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function onDelete(user: UserRowDto) {
    const ok = await confirm({
      title: `${user.email} を削除しますか？`,
      description:
        "ユーザーが所属しているプロジェクトの membership は外れます。最後の owner として残っているプロジェクトがあると削除はできません。",
      confirmLabel: "削除する",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.deleteUser(user.id);
      setError(null);
      reload();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        const payload = e.payload as
          | {
              error?: string;
              projects?: Array<{ projectSlug: string; projectName: string }>;
            }
          | null;
        if (payload?.error === "user_is_last_owner_of" && payload.projects) {
          const list = payload.projects
            .map((p) => `${p.projectName} (/${p.projectSlug})`)
            .join("、");
          setError(
            `${user.email} は次のプロジェクトで最後の owner です: ${list}。先に別のメンバーを owner に昇格させるか、プロジェクトを削除してください。`,
          );
          return;
        }
      }
      setError((e as Error).message);
    }
  }

  function renderProjects(user: UserRowDto, layout: UserListLayout) {
    if (user.projects.length === 0) {
      return layout === "mobile" ? (
        <div class="font-mono text-xs text-fg-subtle">—</div>
      ) : (
        <span class="text-fg-faint">—</span>
      );
    }
    return (
      <div class="flex flex-wrap gap-1">
        {user.projects.map((project) => (
          <a
            key={project.id}
            class="inline-flex items-center gap-1 rounded-md border border-border-subtle bg-bg-subtle px-2 py-0.5 font-mono text-xs text-fg-muted transition hover:border-border hover:text-fg"
            href={adminHref(`/projects/${project.id}`)}
            title={`${project.role} of ${project.name}`}
          >
            <span>{layout === "mobile" ? `/${project.slug}` : project.slug}</span>
            {project.role === "owner" && (
              <span class="text-[10px] uppercase text-fg-subtle">owner</span>
            )}
          </a>
        ))}
      </div>
    );
  }

  return (
    <section class="space-y-10">
      {confirmDialog}
      <PageHeader
        title="ユーザー管理"
        description="プロジェクトを作成し、指定したメールアドレスを owner にします。未登録のアドレスならユーザーも同時に作成し、どちらの場合もサインインリンクを送信します。"
      />

      <div class="space-y-4">
        <SectionHeading level={3} title="プロジェクトを作成" />
        <Card>
          <CardBody>
            <form onSubmit={onInvite} class="grid gap-3 sm:grid-cols-2">
              <Field label="owner のメールアドレス" required>
                <Input
                  type="email"
                  required
                  value={email}
                  onInput={(e) =>
                    setEmail((e.target as HTMLInputElement).value)
                  }
                />
              </Field>
              <Field label="名前 (任意・新規ユーザーのみ)">
                <Input
                  type="text"
                  value={name}
                  onInput={(e) =>
                    setName((e.target as HTMLInputElement).value)
                  }
                />
              </Field>
              <Field label="プロジェクト slug" required>
                <Input
                  type="text"
                  required
                  pattern="[a-z0-9](?:[a-z0-9\-]*[a-z0-9])?"
                  placeholder="landing"
                  monospace
                  value={projectSlug}
                  onInput={(e) =>
                    setProjectSlug((e.target as HTMLInputElement).value)
                  }
                />
              </Field>
              <Field label="プロジェクト名" required>
                <Input
                  type="text"
                  required
                  placeholder="Landing"
                  value={projectName}
                  onInput={(e) =>
                    setProjectName((e.target as HTMLInputElement).value)
                  }
                />
              </Field>
              <div class="flex items-center justify-end sm:col-span-2">
                <Button
                  type="submit"
                  variant="primary"
                  iconLeft={<FolderPlusIcon class="h-4 w-4" />}
                  loading={submitting}
                  disabled={submitting}
                >
                  {submitting ? "作成中…" : "作成してメールを送信"}
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}

      {items === null ? (
        <SkeletonList count={3} rowClass="h-12" />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<UserPlusIcon class="h-5 w-5" />}
          title="ユーザーはまだいません"
          description="上のフォームからプロジェクトを作成してください。"
        />
      ) : (
        <UserList
          items={items}
          getKey={(user) => user.id}
          getEmail={(user) => user.email}
          getEmailVerified={(user) => user.emailVerified}
          extraColumn={{ label: "プロジェクト", render: renderProjects }}
          showMobileVerification
          action={{
            desktopKind: "button",
            desktopLabel: "削除",
            mobileLabel: "削除",
            onAction: onDelete,
          }}
        />
      )}
    </section>
  );
}
